import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileCheck, 
  Shield, 
  Clock, 
  Users,
  Upload,
  Inbox,
  CheckCircle2,
  ArrowRight,
  Building2,
  Truck,
  AlertTriangle,
  TrendingUp,
  Send
} from 'lucide-react';
import { PortalLayout } from './PortalLayout';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
  <div className="glass-card p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className={`text-3xl font-heading font-bold text-${color} mt-1`}>{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        {trend && (
          <div className="flex items-center mt-2 text-xs text-neon-green">
            <TrendingUp className="w-3 h-3 mr-1" />
            {trend}
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
    </div>
  </div>
);

export const ProviderDashboard = () => {
  const { user, organization } = useExchangeAuth();
  const [credentials, setCredentials] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [trustRelationships, setTrustRelationships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [credsRes, inReqsRes, outReqsRes, trustRes] = await Promise.all([
        exchangeApi.getCredentials(),
        exchangeApi.getIncomingRequests(),
        exchangeApi.getOutgoingRequests(),
        exchangeApi.getTrustRelationships()
      ]);
      setCredentials(credsRes.data.credentials || []);
      setIncomingRequests(inReqsRes.data.requests || []);
      setOutgoingRequests(outReqsRes.data.requests || []);
      setTrustRelationships(trustRes.data.trust_relationships || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const verifiedCount = credentials.filter(c => c.status === 'verified').length;
  const expiringCount = credentials.filter(c => c.status === 'expiring_soon').length;
  const pendingIncoming = incomingRequests.filter(r => r.status === 'pending').length;
  const pendingOutgoing = outgoingRequests.filter(r => r.status === 'pending').length;
  const trustedPartners = trustRelationships.filter(t => t.is_active).length;

  // Compliance score (simple calculation)
  const complianceScore = credentials.length > 0 
    ? Math.round((verifiedCount / credentials.length) * 100) 
    : 0;

  return (
    <PortalLayout role="provider">
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              {organization?.legal_name || 'Provider Dashboard'}
            </h1>
            <p className="text-slate-400">Manage company credentials and driver compliance</p>
          </div>
          <div className="flex space-x-3">
            <Link to="/portal/provider/drivers" className="btn-secondary">
              <Users className="w-4 h-4 mr-2" />
              Manage Drivers
            </Link>
            <Link to="/portal/provider/credentials" className="btn-primary">
              <Upload className="w-4 h-4 mr-2" />
              Upload Credential
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Compliance Score"
            value={`${complianceScore}%`}
            icon={Shield}
            color="neon-green"
          />
          <StatCard
            title="Company Credentials"
            value={verifiedCount}
            icon={FileCheck}
            color="neon-cyan"
            subtitle={`${credentials.length} total`}
          />
          <StatCard
            title="Expiring Soon"
            value={expiringCount}
            icon={Clock}
            color={expiringCount > 0 ? 'yellow' : 'slate'}
          />
          <StatCard
            title="Incoming Requests"
            value={pendingIncoming}
            icon={Inbox}
            color={pendingIncoming > 0 ? 'neon-purple' : 'slate'}
          />
          <StatCard
            title="Trusted Partners"
            value={trustedPartners}
            icon={Shield}
            color="neon-green"
          />
        </div>

        {/* Quick Actions Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Incoming Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Incoming Requests</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${
                pendingIncoming > 0 ? 'bg-neon-purple/20 text-neon-purple' : 'bg-white/10 text-slate-400'
              }`}>
                {pendingIncoming} pending
              </span>
            </div>
            {pendingIncoming > 0 ? (
              <div className="space-y-3">
                {incomingRequests.filter(r => r.status === 'pending').slice(0, 3).map((req) => (
                  <div key={req.id} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-white font-medium">{req.requester_name}</p>
                      <span className={`text-xs ${
                        req.urgency === 'urgent' ? 'text-red-400' : 
                        req.urgency === 'high' ? 'text-yellow-400' : 'text-slate-400'
                      }`}>
                        {req.urgency}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {req.credential_types?.length || 0} credential(s) • {req.purpose}
                    </p>
                  </div>
                ))}
                <Link
                  to="/portal/provider/requests"
                  className="text-sm text-neon-purple hover:underline flex items-center"
                >
                  View all requests <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ) : (
              <div className="text-center py-4">
                <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No pending requests</p>
              </div>
            )}
          </motion.div>

          {/* Outgoing Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Outgoing Requests</h3>
              <span className={`text-xs px-2 py-1 rounded-full ${
                pendingOutgoing > 0 ? 'bg-neon-cyan/20 text-neon-cyan' : 'bg-white/10 text-slate-400'
              }`}>
                {pendingOutgoing} pending
              </span>
            </div>
            {outgoingRequests.length > 0 ? (
              <div className="space-y-3">
                {outgoingRequests.slice(0, 3).map((req) => (
                  <div key={req.id} className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-white font-medium">{req.target_name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        req.status === 'approved' || req.status === 'auto_approved' ? 'bg-neon-green/20 text-neon-green' :
                        req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        req.status === 'denied' ? 'bg-red-500/20 text-red-400' :
                        'bg-white/10 text-slate-400'
                      }`}>
                        {req.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {req.credential_types?.length || 0} credential(s)
                    </p>
                  </div>
                ))}
                <Link
                  to="/portal/provider/outgoing"
                  className="text-sm text-neon-cyan hover:underline flex items-center"
                >
                  View all <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ) : (
              <div className="text-center py-4">
                <Send className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No outgoing requests</p>
              </div>
            )}
          </motion.div>

          {/* Trusted Partners */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Trusted Partners</h3>
              <Link to="/portal/provider/trust" className="text-xs text-neon-green hover:underline">
                Manage
              </Link>
            </div>
            {trustedPartners > 0 ? (
              <div className="space-y-3">
                {trustRelationships.filter(t => t.is_active).slice(0, 3).map((trust) => (
                  <div key={trust.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-neon-green/20 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-neon-green" />
                      </div>
                      <div>
                        <p className="text-sm text-white">{trust.trusted_name}</p>
                        <p className="text-xs text-slate-500">
                          {trust.auto_approve_all ? 'Auto-approve all' : `${trust.credential_types?.length || 0} types`}
                        </p>
                      </div>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-neon-green" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Shield className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400 mb-2">No trusted partners</p>
                <Link to="/portal/provider/trust" className="text-xs text-neon-green hover:underline">
                  Add trusted partner
                </Link>
              </div>
            )}
          </motion.div>
        </div>

        {/* Credentials Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Company Credentials</h3>
            <Link to="/portal/provider/credentials" className="text-sm text-neon-green hover:underline">
              View all
            </Link>
          </div>
          
          {credentials.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {credentials.slice(0, 6).map((cred) => (
                <div key={cred.id} className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      cred.status === 'verified' ? 'bg-neon-green/20' :
                      cred.status === 'expiring_soon' ? 'bg-yellow-500/20' :
                      cred.status === 'expired' ? 'bg-red-500/20' :
                      'bg-white/10'
                    }`}>
                      <FileCheck className={`w-5 h-5 ${
                        cred.status === 'verified' ? 'text-neon-green' :
                        cred.status === 'expiring_soon' ? 'text-yellow-400' :
                        cred.status === 'expired' ? 'text-red-400' :
                        'text-slate-400'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{cred.name}</p>
                      <p className="text-xs text-slate-500 capitalize">
                        {cred.credential_type?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      cred.status === 'verified' ? 'bg-neon-green/20 text-neon-green' :
                      cred.status === 'expiring_soon' ? 'bg-yellow-500/20 text-yellow-400' :
                      cred.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                      'bg-white/10 text-slate-400'
                    }`}>
                      {cred.status?.replace(/_/g, ' ')}
                    </span>
                    {cred.expiration_date && (
                      <span className="text-xs text-slate-500">
                        Exp: {new Date(cred.expiration_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">No company credentials uploaded yet</p>
              <Link to="/portal/provider/credentials" className="btn-primary">
                Upload Company Credentials
              </Link>
            </div>
          )}
        </motion.div>

        {/* Expiring Alert */}
        {expiringCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 border-yellow-500/30 bg-yellow-500/5"
          >
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-medium mb-1">Credentials Expiring Soon</h4>
                <p className="text-sm text-slate-400 mb-3">
                  {expiringCount} credential{expiringCount > 1 ? 's' : ''} will expire in the next 30 days. 
                  Update them to maintain compliance and avoid service interruptions.
                </p>
                <Link to="/portal/provider/credentials" className="text-yellow-400 text-sm hover:underline">
                  View expiring credentials →
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </PortalLayout>
  );
};

export default ProviderDashboard;
